// Angular
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';

// Afidium
import { IBreadcrumbItem } from 'libraries/service/breadcrumb/IBreadcrumbItem';
import { ProcessingSmsService } from '../../services/processing-sms.service';
import { SmsBreadcrumbService } from '../../services/sms-breadcrumb.service';
import { WORDING } from './config/wording';
import { ITaskRequest } from 'libraries/service/resources/model/processing/ITaskRequest';
import { ISendSmsResponse } from 'libraries/service/resources/model/processing/ISendSmsResponse';
import { INotification, NotificationType } from 'libraries/interfaces/INotification';
import { ILoader } from 'libraries/component/loader/ILoader';
import { ISmsPreview } from 'app_sasha/shared/interfaces/ISms';
import { ProcessingService } from 'libraries/service/processing/processing.service';
import { ProcessingSmsResourceService } from 'app_sasha/shared/resources/processing-sms-resource/processing-sms-resource.service';
import { TitleService } from 'app_sasha/shared/services/title/title.service';
import { Yii } from 'libraries/interfaces/Yii';
import { LanguageService } from "libraries/service/language/language.service";

@Component({
    selector: 'sms-validation',
    template: `
    <div class="app-wrapper app-sasha">
        <breadcrumb [items]="breadcrumbItems"></breadcrumb>
        <card>
            <div class="card-header-content">
                <h1>{{ wording.header }}</h1>

                <div>
                    <back-button></back-button>
                    <btn [label]="wording.previousView" (click)="smsFormView()" type="actionDo"></btn>
                    <btn [label]="wording.validate" (click)="sendSms()" type="actionPrincipal"></btn>
                </div>
            </div>

            <div class="card-body-content card-marge-inside">
                <processing-validation
                    [codes]="codes"
                    [syscodes]="syscodes"
                    (onChangeFolder)="loadBooking($event)">
                    <notification class="sep-top sep-bottom" *ngIf="notification.active"
                        [type]="notification.type">
                        {{ notification.message }}
                    </notification>

                    <div *ngIf="smsValidation && !loader.active && !notification.active" class="flex">
                        <div *ngIf="smsValidation.body" class="sep-top sep-left sms-bubble">
                            <p [innerHTML]="smsValidation.body"></p>
                        </div>

                        <div *ngIf="sms" class="sms-info sep-top sep-left">
                            <div>{{ wording.replyTo }} :
                                <span
                                    *ngFor="let number of smsValidation.phoneNumber; let last = last"
                                    class="replyTo">
                                    {{ number }}<ng-container *ngIf="!last">, </ng-container>
                                </span>
                            </div>

                            <div class="sep-top">
                                {{ wording.characters }} : <span class="sms-info-value">{{ sms.length }}</span>
                            </div>
                            <div>
                                {{ wording.numberOfSms }} :
                                <span class="sms-info-value">{{ sms.nbrOfSms }}
                                    <ng-container *ngIf="smsValidation.phoneNumber.length > 1">
                                        {{ wording.recipient }}</ng-container>
                                </span>
                            </div>
                            <div *ngIf="smsValidation.phoneNumber.length > 1">
                                {{ wording.numberOfRecipient }} :
                                <span class="sms-info-value">{{ smsValidation.phoneNumber.length }}</span>
                            </div>
                        </div>
                    </div>

                    <loader *ngIf="loader.active" [message]="loader.message"></loader>
                </processing-validation>
            </div>
        </card>
    `,
    styles: [`
        notification {
            display: block;
        }

        .sms-info-value, .replyTo {
            font-weight: bold;
        }

        .sms-bubble {
            display: flex;
            position: relative;
            width: 350px;
            background-color: #e4e4e4;
            border-radius: 5px;
            line-height: 1.5em;
            padding: 1em;
            text-align: left;
            white-space: pre-wrap;
            > p {
                margin: 0;
            }
        }

        .sms-bubble:after {
        	content: ' ';
        	position: absolute;
        	left: -20px;
        	right: auto;
            top: 0px;
        	bottom: auto;
        	width: 0;
        	height: 0;
            border: 22px solid;
        	border-color: #e4e4e4 transparent transparent;
        }
    `]
})
export class SmsValidationComponent implements OnInit {
    public wording = WORDING;

    public syscodeIndex: number = 0;
    public syscodesLength: number = 1;
    public selectedSyscode: string;
    public syscodes: string[] = [];
    public codes: string[] = [];

    public smsValidation: ISmsPreview;
    public breadcrumbItems: IBreadcrumbItem[] = [];
    public notification: INotification = {
        active: false,
        type: NotificationType.Error,
        message: null
    };

    public loader: ILoader = {
        active: false,
        message: null
    };

    public sms: {
        length: number;
        nbrOfSms: number;
    };

    constructor(
        private readonly router: Router,
        private readonly processingService: ProcessingService,
        private readonly processingSmsService: ProcessingSmsService,
        private readonly smsBreadcrumbService: SmsBreadcrumbService,
        private readonly processingSmsResourceService: ProcessingSmsResourceService,
        private readonly titleService: TitleService,
        private readonly languageService: LanguageService,
    ) { }

    public ngOnInit(): void {
        this.breadcrumbItems = this.smsBreadcrumbService.breadcrumbValidation();
        this.codes = this.processingService.bookings.map((booking) => booking.code);
        this.syscodes = this.processingService.bookings.map((booking) => booking.syscode);
        this.titleService.setTitle(Yii.t("wui-sasha-js", "Prévisualisation des SMS"));

        // Get initial preview
        const selectedSyscode: string = this.syscodes[0];
        this.loadBooking(selectedSyscode);
    }

    public async smsFormView(): Promise<void> {
        await this.router.navigate(['/sasha/processing-sms/form'], { queryParamsHandling: 'preserve' });
    }

    public loadBooking(syscode: string): void {
        this.loader = {
            active: true,
            message: this.wording.loadingSms
        };

        this.notification.active = false;

        const values = this.processingSmsService.form;

        this.processingSmsResourceService.getPreview(syscode, values)
            .subscribe(
                (sms: ISmsPreview) => {
                    this.smsValidation = {
                        ...this.smsValidation,
                        ...sms
                    };

                    const smsLength = sms.body.length;

                    this.sms = {
                        length: smsLength,
                        nbrOfSms: smsLength / 160 < 1 ? 1 : Math.ceil(smsLength / 160)
                    };
                },
                (error: Error) => {
                    this.loader.active = false;

                    this.notification = {
                        active: true,
                        type: NotificationType.Error,
                        message: error.message
                    };
                },
                () => this.loader.active = false
            );
    }

    public sendSms(): void {
        const sms = this.processingSmsService.form;
        const task: ITaskRequest[] =
            this.processingService.bookings.map((booking) => ({ 'booking': { 'syscode': booking.syscode } }));

        this.loader = {
            active: true,
            message: this.wording.loadingSendSms
        };

        this.processingSmsResourceService.sendSms({
            title: this.processingSmsService.form.modelName,
            type: 'sms',
            task,
            parameter: {
                stopClause: sms.stopClause,
                body: sms.body,
                modelName: this.processingSmsService.form.modelName,
                language: this.languageService.userLanguage,
            }
        }).subscribe(
            (response: ISendSmsResponse) => this.router.navigate(['/sasha/processing/', response.id]),
            (error: Error) => {
                this.loader.active = false;

                this.notification = {
                    active: true,
                    type: NotificationType.Error,
                    message: error.message
                };
            },
            () => this.loader.active = false
        );
    }
}
